import posthog from "posthog-js";
import { useEffect, useRef } from "react";

import { getUsdvMigrationCurrentStep } from "../widgets/usdv-migration/model";
import type { UsdvMigrationCardConfig } from "../widgets/usdv-migration/types";

import { analyticsContext } from "./posthog";
import {
    captureWalletEvent,
    eventError,
    eventId,
    type EventOwner,
    type EventSurface,
    migrationStep,
    type WalletEventProperties
} from "./walletEvents";
import { walletJourney } from "./walletJourney";

export function useWalletMenuAnalytics(
    open: boolean,
    address: string | undefined,
    surface: EventSurface,
    migration?: UsdvMigrationCardConfig
): {
    copy: (action: () => Promise<void>) => Promise<boolean>;
    disconnect: (
        action: () => Promise<void>,
        guard: (() => boolean | Promise<boolean>) | undefined,
        dirty: boolean
    ) => Promise<boolean>;
    openMigration: () => void;
    viewMigrationStep: (
        index: number,
        state: "ready" | "pending" | "failed" | "succeeded" | "cooldown",
        remaining: number
    ) => void;
    closeMigration: (reason?: "back" | "close_button" | "completed") => void;
    close: (reason: string) => void;
    balances: (state: "initial_loading" | "loaded" | "refreshing", hasUsdv?: boolean, hasSolo?: boolean) => void;
} {
    const menu = useRef<{
        properties: WalletEventProperties;
        owner: EventOwner;
        started: number;
        address: string;
        migration?: { properties: WalletEventProperties; started: number };
    } | null>(null);
    const keys = useRef(new Map<string, string>());
    const reason = useRef("unknown");
    const migrationRef = useRef(migration);
    migrationRef.current = migration;
    const closeMigration = (why = "unknown"): void => {
        const current = menu.current;
        if (!current?.migration) return;
        captureWalletEvent(
            "migration:closed",
            {
                ...current.properties,
                ...current.migration.properties,
                close_method: why === "escape" ? "escape_key" : why,
                transaction_pending: migrationRef.current?.stepFlow?.phase === "pending",
                duration_ms: performance.now() - current.migration.started
            },
            current.owner
        );
        current.migration = undefined;
        keys.current.delete("step");
    };
    useEffect(() => {
        const previous = menu.current;
        if (previous && (!open || previous.address !== address)) {
            const why = !address ? "disconnect" : previous.address !== address ? "account_change" : reason.current;
            const lastView = previous.migration ? "migration" : "account";
            closeMigration(why === "account_change" ? why : "menu_closed");
            captureWalletEvent(
                "wallet:menu_closed",
                {
                    ...previous.properties,
                    close_method: why === "escape" ? "escape_key" : why,
                    last_view: lastView,
                    duration_ms: performance.now() - previous.started
                },
                previous.owner
            );
            menu.current = null;
            keys.current.clear();
            reason.current = "unknown";
        }
        if (open && address && !menu.current) {
            const properties = { entry_point: "wallet_menu" as const, menu_id: eventId(), surface };
            menu.current = { properties, owner: walletJourney.owner(), started: performance.now(), address };
            captureWalletEvent("wallet:menu_opened", properties, menu.current.owner);
        }
    }, [open, address, surface]);
    useEffect(() => {
        const current = menu.current;
        if (!open || !current || !migration || current.migration) return;
        const step = migrationStep(getUsdvMigrationCurrentStep(migration.plan)?.index);
        const state =
            migration.analytics?.state ??
            (migration.queueLoadRecovery
                ? migration.queueLoadRecovery.status === "loading"
                    ? "loading"
                    : "check_failed"
                : migration.state.phase === "success"
                  ? "completed"
                  : "available");
        const properties: WalletEventProperties = {
            ...current.properties,
            ...migration.analytics?.properties,
            migration_type: "legacy_usdv",
            step,
            state
        };
        const key = JSON.stringify([step, state]);
        if (keys.current.get("card") === key) return;
        keys.current.set("card", key);
        captureWalletEvent("migration:card_viewed", { ...properties, entry_step: step }, current.owner);
    });
    return {
        close: (why): void => {
            reason.current = why;
        },
        closeMigration,
        viewMigrationStep: (index, visibleState, remaining): void => {
            const current = menu.current;
            if (!open || !current?.migration) return;
            const configured = migration?.analytics?.stepState;
            const state =
                visibleState === "ready" && configured && configured !== "succeeded" ? configured : visibleState;
            const key = JSON.stringify([index, state]);
            if (keys.current.get("step") === key) return;
            keys.current.set("step", key);
            captureWalletEvent(
                "migration:step_viewed",
                {
                    ...current.properties,
                    ...migration?.analytics?.properties,
                    ...current.migration.properties,
                    step: migrationStep(index),
                    state,
                    remaining_step_count: remaining
                },
                current.owner
            );
        },
        openMigration: (): void => {
            const current = menu.current;
            if (!current || current.migration) return;
            const properties = {
                ...migration?.analytics?.onOpen(),
                migration_type: "legacy_usdv" as const,
                view_id: eventId(),
                entry_step: migrationStep(getUsdvMigrationCurrentStep(migration!.plan)?.index)
            };
            current.migration = { properties, started: performance.now() };
            keys.current.delete("card");
            captureWalletEvent("migration:opened", { ...current.properties, ...properties }, current.owner);
        },
        copy: async (action): Promise<boolean> => {
            const current = menu.current;
            if (!current) return false;
            const properties = { ...current.properties, operation_id: eventId() };
            captureWalletEvent("wallet:address_copy_requested", properties, current.owner);
            try {
                await action();
                captureWalletEvent("wallet:address_copied", properties, current.owner);
                return true;
            } catch (error) {
                captureWalletEvent(
                    "wallet:address_copy_failed",
                    { ...properties, ...eventError(error) },
                    current.owner
                );
                return false;
            }
        },
        disconnect: async (action, guard, dirty): Promise<boolean> => {
            const result = await walletJourney.disconnect(
                action,
                "user_action",
                guard,
                menu.current?.properties,
                dirty
            );
            if (result) {
                reason.current = "disconnect";
                if (analyticsContext().app !== "main") {
                    posthog.capture("wallet_disconnected", { trigger: "user_action" });
                    posthog.reset();
                }
            }
            return result;
        },
        balances: (state, hasUsdv, hasSolo): void => {
            const current = menu.current;
            if (!current || !open || current.migration || keys.current.get("balances") === state) return;
            keys.current.set("balances", state);
            captureWalletEvent(
                "wallet:balances_viewed",
                { ...current.properties, state, has_usdv: hasUsdv, has_solo: hasSolo },
                current.owner
            );
        }
    };
}
